import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { type MessageDescriptorV2 as MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import messages from './messages';

type Props = {
	heading: MessageDescriptor;
	description: MessageDescriptor;
	footer: ReactNode;
	icon: ReactNode;
	teams: MessageDescriptor[];
	onClick: () => void;
	product: string;
};

export const ProductTile = ({
	heading,
	description,
	icon,
	footer,
	teams,
	onClick,
	product,
}: Props) => {
	const { formatMessage } = useIntl();

	return (
		<Wrapper data-testid="project-create-importer-upload-flow.ui.select-product-screen.product-tile.wrapper">
			<Button
				appearance="subtle"
				onClick={onClick}
				testId={`project-create-importer-upload-flow.ui.select-product-screen.product-tile.button-${product}`}
			>
				<ContentWrapper>
					<Box xcss={iconWrapperStyles}>{icon}</Box>
					<Heading>{formatMessage(heading)}</Heading>
					<Description>{formatMessage(description)}</Description>
				</ContentWrapper>
				<FooterWrapper>
					<Line />
					<span>{formatMessage(messages.idealFor)}</span>
					<TeamLozengeWrapper>
						{teams.map((team) => (
							<Lozenge key={formatMessage(team)} appearance="default">
								{formatMessage(team)}
							</Lozenge>
						))}
					</TeamLozengeWrapper>
					{footer}
				</FooterWrapper>
			</Button>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	margin: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	borderRadius: '8px',
	boxShadow: token(
		'elevation.shadow.raised',
		'0px 1px 1px rgba(9, 30, 66, 0.25),0px 0px 1px rgba(9, 30, 66, 0.31)',
	),
	'&:hover': {
		boxShadow: token(
			'elevation.shadow.overflow',
			'0px 0px 8px rgba(9, 30, 66, 0.16),0px 0px 1px rgba(9, 30, 66, 0.12)',
		),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		width: '320px',
		height: '100%',
		padding: token('space.200', '16px'),
		borderRadius: '8px',
		alignItems: 'stretch',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> span': {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'space-between',
		},
		'&:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			backgroundColor: token('elevation.surface', colors.N0),
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	margin: `${token('space.300', '24px')} ${token('space.100', '8px')} ${token(
		'space.100',
		'8px',
	)} ${token('space.100', '8px')}`,
	minHeight: '176px',
});

const iconWrapperStyles = xcss({
	marginBottom: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Heading = styled.h2({
	fontWeight: '600',
	fontSize: '16px',
	margin: '0px',
	textAlign: 'center',
	width: '100%',
	overflowWrap: 'break-word',
	whiteSpace: 'normal',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.p({
	maxWidth: '100%',
	marginTop: token('space.200', '16px'),
	textAlign: 'center',
	whiteSpace: 'initial',
	lineHeight: '20px',
	fontWeight: 400,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N400),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Line = styled.div({
	height: token('space.050', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.gray.subtlest', colors.N30),
	borderRadius: '1px',
	marginBottom: token('space.300', '24px'),
	marginTop: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TeamLozengeWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'wrap',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		margin: token('space.050', '4px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	color: "token('color.text.subtlest', colors.N200)",
	fontWeight: 400,
	fontSize: '14px',
});
